import { debounce } from "./debounce";
import { EventEmitter } from "./eventEmitter";
export class CMap {
    constructor(dict) {
        this._dict = {};
        if (dict) {
            this._dict = Object.assign({}, dict._dict);
        }
    }
    get(key) {
        const k = String(key);
        return this._dict[k];
    }
    set(key, value) {
        const k = String(key);
        this._dict[k] = value;
        return this;
    }
    has(key) {
        return this._dict.hasOwnProperty(String(key));
    }
    delete(key) {
        const k = String(key);
        delete this._dict[k];
    }
    clear() {
        this._dict = {};
    }
}
export const CACHE_BOX_CHANGE_EVENT_NAME = 'change';
const MAX_SCROLL_DIRECTION_POOL = 50, CLEAR_SCROLL_DIRECTION_TO = 10, DIR_BACK = '-1', DIR_NONE = '0', DIR_FORWARD = '1';
/**
 * Cache map.
 * Emits a change event on each mutation.
 * @link https://github.com/DjonnyX/rcx-virtual-list/tree/main/src/lib/src/utils/cacheMap.ts
 * @author Evgenii Grebennikov
 * @email djonnyx@gmail.com
 */
export class CacheMap extends EventEmitter {
    get delta() {
        return this._delta;
    }
    set deltaDirection(v) {
        this._deltaDirection = v;
        this._scrollDirection = this.calcScrollDirection(v);
    }
    get deltaDirection() {
        return this._deltaDirection;
    }
    get scrollDirection() {
        return this._scrollDirection;
    }
    get version() {
        return this._version;
    }
    constructor() {
        super();
        this._map = new CMap();
        this._snapshot = new CMap();
        this._version = 0;
        this._previousVersion = this._version;
        this._delta = 0;
        this._deltaDirection = 0;
        this._scrollDirectionCache = [];
        this._scrollDirection = 0;
        this._clearScrollDirectionDebounce = debounce(() => {
            while (this._scrollDirectionCache.length > CLEAR_SCROLL_DIRECTION_TO) {
                this._scrollDirectionCache.shift();
            }
        }, 10);
        this.lifeCircle();
    }
    changesDetected() {
        return this._version !== this._previousVersion;
    }
    stopLifeCircle() {
        clearTimeout(this._lifeCircleTimeout);
    }
    nextTick(cb) {
        if (this._disposed) {
            return;
        }
        this._lifeCircleTimeout = setTimeout(() => {
            cb();
        });
        return this._lifeCircleTimeout;
    }
    lifeCircle() {
        this.fireChangeIfNeed();
        this.lifeCircleDo();
    }
    lifeCircleDo() {
        this._previousVersion = this._version;
        this.nextTick(() => {
            this.lifeCircle();
        });
    }
    clearScrollDirectionCache() {
        this._clearScrollDirectionDebounce.execute();
    }
    calcScrollDirection(v) {
        while (this._scrollDirectionCache.length >= MAX_SCROLL_DIRECTION_POOL) {
            this._scrollDirectionCache.shift();
        }
        this._scrollDirectionCache.push(v);
        const dict = { [DIR_BACK]: 0, [DIR_NONE]: 0, [DIR_FORWARD]: 0 };
        for (let i = 0, l = this._scrollDirectionCache.length, li = l - 1; i < l; i++) {
            const dir = String(this._scrollDirectionCache[i]);
            dict[dir] += 1;
            if (i === li) {
                for (let d in dict) {
                    if (d === String(v)) {
                        continue;
                    }
                    dict[d] -= 1;
                }
            }
        }
        if (dict[DIR_BACK] > dict[DIR_NONE] && dict[DIR_BACK] > dict[DIR_FORWARD]) {
            return -1;
        }
        else if (dict[DIR_FORWARD] > dict[DIR_BACK] && dict[DIR_FORWARD] > dict[DIR_NONE]) {
            return 1;
        }
        return 0;
    }
    bumpVersion() {
        if (this.changesDetected()) {
            return;
        }
        const v = this._version === Number.MAX_SAFE_INTEGER ? 0 : this._version + 1;
        this._version = v;
    }
    fireChangeIfNeed() {
        if (this.changesDetected()) {
            this.dispatch(CACHE_BOX_CHANGE_EVENT_NAME, this.version);
        }
    }
    set(id, bounds) {
        if (this._map.has(id)) {
            const b = this._map.get(id), bb = bounds;
            if (b.width === bb.width && b.height === bb.height) {
                return this._map;
            }
            return this._map;
        }
        const v = this._map.set(id, bounds);
        this.bumpVersion();
        return v;
    }
    has(id) {
        return this._map.has(id);
    }
    get(id) {
        return this._map.get(id);
    }
    snapshot() {
        this._snapshot = new CMap(this._map);
    }
    dispose() {
        super.dispose();
        this.stopLifeCircle();
        this._snapshot.clear();
        this._map.clear();
    }
}
